import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Check } from "lucide-react";

import { IMG, SERVICES } from "../lib/assets";
import { Reveal, AnimatedHeading } from "../components/Reveal";
import { PageShell, PageHeader } from "../components/PageShell";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const HOURS = [
  ["Monday – Friday", "10:00 – 20:00"],
  ["Saturday", "09:00 – 21:00"],
  ["Sunday", "10:00 – 18:00"],
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const update = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <PageShell>
      <PageHeader
        overline="Get in Touch"
        title="Reserve your moment."
        subtitle="Tell us what you dream of, and our team will craft the perfect appointment for you."
        image={IMG.interiorMirror}
      />

      <section className="py-24">
        <div className="mx-auto grid max-w-wide grid-cols-1 gap-16 px-6 sm:px-12 lg:grid-cols-[1.618fr_1fr] lg:gap-24">
          {/* Form */}
          <div>
            <Reveal>
              <p className="overline text-gold">Booking Enquiry</p>
            </Reveal>
            <AnimatedHeading
              text="Let's begin."
              className="mt-4 font-display text-5xl font-light text-cream-50 sm:text-6xl"
            />

            {status === "success" ? (
              <Reveal>
                <div
                  data-testid="contact-success"
                  className="mt-10 flex items-center gap-4 rounded-2xl border border-gold/40 bg-ink-800 p-8"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold text-ink-900">
                    <Check size={24} />
                  </div>
                  <div>
                    <p className="font-display text-2xl text-gold">Thank you!</p>
                    <p className="text-cream-50">
                      Your enquiry is woven in gold. We'll be in touch shortly.
                    </p>
                  </div>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={submit} className="mt-10 flex flex-col gap-6" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Full Name" name="name" value={form.name} onChange={update} required testid="contact-name" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={update} required testid="contact-email" />
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Phone" name="phone" value={form.phone} onChange={update} testid="contact-phone" />
                  <div className="flex flex-col gap-2">
                    <label className="overline text-cream-50/60">Service</label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={update}
                      data-testid="contact-service"
                      className="border-b border-cream-50/20 bg-transparent py-3 text-cream-50 outline-none transition-colors focus:border-gold"
                    >
                      <option value="" className="bg-ink-900">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.name} className="bg-ink-900">
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="overline text-cream-50/60">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={update}
                    required
                    rows={4}
                    data-testid="contact-message"
                    placeholder="Tell us about your occasion or the look you envision…"
                    className="resize-none border-b border-cream-50/20 bg-transparent py-3 text-cream-50 outline-none transition-colors placeholder:text-cream-50/30 focus:border-gold"
                  />
                </div>

                {status === "error" && (
                  <p data-testid="contact-error" className="text-terracotta">
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="contact-submit"
                  className="mt-2 self-start rounded-full bg-gold px-10 py-4 text-xs uppercase tracking-[0.2em] text-ink-900 transition-colors hover:bg-antique disabled:opacity-50"
                >
                  {status === "sending" ? "Sending…" : "Send Enquiry"}
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <div className="flex flex-col gap-10">
            <Reveal>
              <div>
                <h3 className="overline text-gold">Visit the Atelier</h3>
                <ul className="mt-6 flex flex-col gap-5 text-cream-50/80">
                  <li className="flex items-start gap-4">
                    <MapPin size={20} className="mt-1 shrink-0 text-gold" />
                    <span>14 Heritage Lane, Jaipur,<br />Rajasthan 302001, India</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <Phone size={20} className="shrink-0 text-gold" />
                    <a href="tel:+919000000000" className="link-underline">+91 90000 00000</a>
                  </li>
                  <li className="flex items-center gap-4">
                    <Mail size={20} className="shrink-0 text-gold" />
                    <a href="mailto:hello@zayazari.com" className="link-underline">hello@zayazari.com</a>
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-gold/20 bg-ink-800/40 p-8">
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-gold" />
                  <h3 className="font-display text-2xl text-cream-50">Opening Hours</h3>
                </div>
                <ul className="mt-6 flex flex-col gap-3">
                  {HOURS.map(([d, t]) => (
                    <li key={d} className="flex items-center justify-between border-b border-cream-50/10 pb-3 text-sm">
                      <span className="text-cream-50/70">{d}</span>
                      <span className="text-gold">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testid }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="overline text-cream-50/60">
        {label}
        {required && <span className="text-gold"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="border-b border-cream-50/20 bg-transparent py-3 text-cream-50 outline-none transition-colors focus:border-gold"
      />
    </div>
  );
}
